import { type CollectedUploadFile } from "./types";

interface WebkitFileEntry {
  readonly isDirectory: false;
  readonly isFile: true;
  readonly name: string;
  readonly file: (success: (file: File) => void, error?: () => void) => void;
}

interface WebkitDirectoryEntry {
  readonly isDirectory: true;
  readonly isFile: false;
  readonly name: string;
  readonly createReader: () => WebkitDirectoryReader;
}

interface WebkitDirectoryReader {
  readonly readEntries: (success: (entries: WebkitEntry[]) => void, error?: () => void) => void;
}

type WebkitEntry = WebkitFileEntry | WebkitDirectoryEntry;

type FileWithPath = File & { readonly path?: string };

const containsControlCharacter = (value: string): boolean =>
  Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || (codePoint >= 127 && codePoint <= 159);
  });

export class DirectoryTraversalUnsupportedError extends Error {
  readonly collectedFiles: readonly CollectedUploadFile[];

  constructor(collectedFiles: readonly CollectedUploadFile[] = []) {
    super("This browser cannot read dropped directories. Individual files can still be uploaded.");
    this.name = "DirectoryTraversalUnsupportedError";
    this.collectedFiles = collectedFiles;
  }
}

export class InvalidDroppedPathError extends Error {
  constructor() {
    super("Dropped path contains an invalid segment.");
    this.name = "InvalidDroppedPathError";
  }
}

const validatePathSegment = (segment: string): string => {
  const trimmedSegment = segment.trim();
  if (
    trimmedSegment.length === 0 ||
    trimmedSegment === "." ||
    trimmedSegment === ".." ||
    segment.includes("/") ||
    trimmedSegment.includes("\\") ||
    containsControlCharacter(segment)
  ) {
    throw new InvalidDroppedPathError();
  }
  return trimmedSegment;
};

const validateRelativePath = (path: string): string[] => path.split("/").map(validatePathSegment);

const toCollectedFile = (file: File, path: string): CollectedUploadFile => {
  const filename = validatePathSegment(file.name);
  const segments = validateRelativePath(path || file.name);
  if (segments.at(-1) !== filename) throw new InvalidDroppedPathError();
  const relativePath = segments.join("/");
  return {
    file,
    relativePath,
    directoryPath: segments.slice(0, -1).join("/"),
  };
};

const readFileEntry = async (entry: WebkitFileEntry): Promise<File> => {
  try {
    return await new Promise((resolve, reject) => {
      entry.file(resolve, () => reject(new DirectoryTraversalUnsupportedError()));
    });
  } catch (error) {
    if (error instanceof DirectoryTraversalUnsupportedError) throw error;
    throw new DirectoryTraversalUnsupportedError();
  }
};

const readDirectoryEntries = async (entry: WebkitDirectoryEntry): Promise<WebkitEntry[]> => {
  let reader: WebkitDirectoryReader;
  try {
    reader = entry.createReader();
  } catch {
    throw new DirectoryTraversalUnsupportedError();
  }

  const entries: WebkitEntry[] = [];
  while (true) {
    let batch: WebkitEntry[];
    try {
      batch = await new Promise<WebkitEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, () => reject(new DirectoryTraversalUnsupportedError()));
      });
    } catch (error) {
      if (error instanceof DirectoryTraversalUnsupportedError) throw error;
      throw new DirectoryTraversalUnsupportedError();
    }
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
};

const collectEntry = async (entry: WebkitEntry, parentSegments: readonly string[]): Promise<CollectedUploadFile[]> => {
  if (entry.isFile) {
    const entryName = validatePathSegment(entry.name);
    const file = await readFileEntry(entry);
    if (validatePathSegment(file.name) !== entryName) throw new InvalidDroppedPathError();
    return [toCollectedFile(file, [...parentSegments, entryName].join("/"))];
  }

  const segments = [...parentSegments, validatePathSegment(entry.name)];
  const children = await readDirectoryEntries(entry);
  const collected: CollectedUploadFile[] = [];
  for (const child of children) {
    try {
      collected.push(...(await collectEntry(child, segments)));
    } catch (error) {
      if (error instanceof InvalidDroppedPathError) throw error;
      const partialFiles =
        error instanceof DirectoryTraversalUnsupportedError ? error.collectedFiles : ([] as CollectedUploadFile[]);
      throw new DirectoryTraversalUnsupportedError(deduplicateFiles([...collected, ...partialFiles]));
    }
  }
  return collected;
};

const getEntry = (item: DataTransferItem): WebkitEntry | null => {
  const webkitItem = item as unknown as { webkitGetAsEntry?: () => WebkitEntry | null };
  if (typeof webkitItem.webkitGetAsEntry !== "function") return null;
  return webkitItem.webkitGetAsEntry();
};

const getFallbackPath = (file: File): string => {
  if (file.webkitRelativePath) return file.webkitRelativePath;
  const libraryPath = (file as FileWithPath).path;
  if (libraryPath === `./${file.name}` || libraryPath === `/${file.name}`) return file.name;
  return libraryPath || file.name;
};

const deduplicateFiles = (files: readonly CollectedUploadFile[]): CollectedUploadFile[] => {
  const byRelativePath = new Map<string, CollectedUploadFile>();
  for (const file of files) {
    if (!byRelativePath.has(file.relativePath)) byRelativePath.set(file.relativePath, file);
  }
  return [...byRelativePath.values()];
};

export const collectSelectedFiles = (files: readonly File[]): CollectedUploadFile[] =>
  deduplicateFiles(files.map(file => toCollectedFile(file, getFallbackPath(file))));

export const collectDroppedFiles = async (dataTransfer: DataTransfer): Promise<CollectedUploadFile[]> => {
  const items = Array.from(dataTransfer.items ?? []).filter(item => item.kind === "file");
  const collected: CollectedUploadFile[] = [];
  let hasUnresolvedTraversalFailure = false;
  let recoverableOrdinaryItemFailures = 0;

  for (const item of items) {
    let entry: WebkitEntry | null;
    try {
      entry = getEntry(item);
    } catch {
      hasUnresolvedTraversalFailure = true;
      continue;
    }

    if (entry) {
      try {
        collected.push(...(await collectEntry(entry, [])));
      } catch (error) {
        if (error instanceof InvalidDroppedPathError) throw error;
        if (error instanceof DirectoryTraversalUnsupportedError) collected.push(...error.collectedFiles);
        if (entry.isDirectory) {
          hasUnresolvedTraversalFailure = true;
        } else {
          recoverableOrdinaryItemFailures += 1;
        }
      }
      continue;
    }

    try {
      const file = item.getAsFile();
      if (file) {
        collected.push(toCollectedFile(file, getFallbackPath(file)));
      } else {
        recoverableOrdinaryItemFailures += 1;
      }
    } catch (error) {
      if (error instanceof InvalidDroppedPathError) throw error;
      if (error instanceof DirectoryTraversalUnsupportedError) collected.push(...error.collectedFiles);
      recoverableOrdinaryItemFailures += 1;
    }
  }

  const collectedBeforeFallback = deduplicateFiles(collected);
  let fallbackAddedFiles = 0;
  if (items.length === 0 || hasUnresolvedTraversalFailure || recoverableOrdinaryItemFailures > 0) {
    for (const fallbackFile of collectSelectedFiles(Array.from(dataTransfer.files ?? []))) {
      if (
        !collectedBeforeFallback.some(collectedFile => collectedFile.relativePath === fallbackFile.relativePath) &&
        !collected.some(collectedFile => collectedFile.relativePath === fallbackFile.relativePath)
      ) {
        fallbackAddedFiles += 1;
      }
      collected.push(fallbackFile);
    }
  }

  const deduplicated = deduplicateFiles(collected);
  if (hasUnresolvedTraversalFailure || fallbackAddedFiles < recoverableOrdinaryItemFailures) {
    throw new DirectoryTraversalUnsupportedError(deduplicated);
  }
  return deduplicated;
};
